import { useState, useEffect, useCallback } from 'react';
import { ONLINE_KITCHENS_WITH_FOOD_URL } from '../../config/api';
import { authenticatedFetch, getFirstImageUrl } from './apiHelpers';
import { getStoredCoords } from './location';

export interface KitchenSummary {
  kitchen_id: number;
  kitchen_name: string;
  kitchen_photo: string;
  kitchen_type: string;
  kitchen_category: string;
  city: string;
  state: string;
  country: string;
  status: boolean;
  made_by: string;
}

export interface FoodItem {
  id: number;
  food_name: string;
  description: string;
  price: number;
  category: string;
  phone_number?: string;
  pickup_location?: string;
  serving_size?: string;
  preparation_time?: string;
  available_quantity: number;
  image_url: string;
  user_id?: number;
  kitchen_id: number;
  kitchen_name: string;
  average_rating?: number;
  review_count?: number;
  stock_status?: string;
  badges?: string[];
  distance_km?: number | null;
  eta?: string;
  delivery_status?: string;
  trending_score?: number;
  rating_score?: number;
  total_reviews?: number;
  total_orders?: number;
}

interface HookResult {
  kitchens: KitchenSummary[];
  meals: FoodItem[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

const REFRESH_EVENTS = [
  'kitchenStatusChanged',
  'kitchenCreated',
  'kitchenDeleted',
  'foodItemCreated',
  'foodAvailabilityChanged',
  'kitchenApprovalChanged',
];

export const useOnlineKitchensWithFood = (locationVersion = 0, radius: number = 15): HookResult => {
  const [kitchens, setKitchens] = useState<KitchenSummary[]>([]);
  const [meals, setMeals] = useState<FoodItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let url = ONLINE_KITCHENS_WITH_FOOD_URL;
      const coords = getStoredCoords();
      if (coords) {
        url = `${url}?user_lat=${coords.lat}&user_lon=${coords.lon}&radius=${radius}`;
      }

      const res = await authenticatedFetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          setError('Your session has expired. Please log in again.');
        } else {
          setError('Failed to load kitchens and food. Please try again later.');
        }
        setIsLoading(false);
        return;
      }

      const body = await res.json();

      // API returns { success, kitchens: [...] } — extract the array
      const kitchenList: any[] = Array.isArray(body) ? body : (body?.kitchens ?? null);

      if (!Array.isArray(kitchenList)) {
        setError('Unexpected response from server.');
        setIsLoading(false);
        return;
      }

      // Filter out blocked (status=false) and unapproved kitchens as a client-side safety net
      const safeKitchens = kitchenList.filter((k) => k.status !== false && k.is_approved !== false);

      const derivedKitchens: KitchenSummary[] = safeKitchens.map((k) => ({
        kitchen_id: k.kitchen_id,
        kitchen_name: k.kitchen_name,
        kitchen_photo: k.kitchen_photo,
        kitchen_type: k.kitchen_type,
        kitchen_category: k.kitchen_category,
        city: k.city,
        state: k.state,
        country: k.country,
        status: k.status,
        made_by: k.made_by,
      }));

      const derivedMeals: FoodItem[] = safeKitchens.flatMap((k) =>
        (k.food_items || []).map((item: any) => ({
          id: item.food_id,
          food_name: item.food_name,
          description: item.description,
          price: item.price,
          category: item.category,
          phone_number: item.phone_number,
          pickup_location: item.pickup_location,
          serving_size: item.serving_size,
          preparation_time: item.preparation_time,
          available_quantity: item.available_quantity,
          image_url: getFirstImageUrl(item.image_urls),
          user_id: item.user_id,
          kitchen_id: k.kitchen_id,
          kitchen_name: k.kitchen_name,
          average_rating: item.average_rating,
          review_count: item.review_count,
          stock_status: item.stock_status,
          badges: item.badges,
          distance_km: item.distance_km ?? null,
          eta: item.eta,
          delivery_status: item.delivery_status,
          trending_score: item.trending_score,
          rating_score: item.rating_score,
          total_reviews: item.total_reviews,
          total_orders: item.total_orders,
        }))
      );

      setKitchens(derivedKitchens);
      setMeals(derivedMeals);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  }, [radius]);

  useEffect(() => {
    fetchData();
    REFRESH_EVENTS.forEach((ev) => window.addEventListener(ev, fetchData));
    return () => {
      REFRESH_EVENTS.forEach((ev) => window.removeEventListener(ev, fetchData));
    };
  }, [fetchData, locationVersion]);

  return { kitchens, meals, isLoading, error, refresh: fetchData };
};
